import React, { useState, useEffect } from 'react';
import { Search, Download, Calendar, TrendingUp, Filter, Users } from 'lucide-react';
import axios from 'axios';

const AttendanceReport = () => {
  const [students, setStudents] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [courses, setCourses] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [sections, setSections] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ 
    from_date: '', 
    to_date: '',
    course_id: '',
    semester_id: '',
    section_id: ''
  });
  const [loading, setLoading] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchCourses();
    fetchSemesters();
    fetchSections();
  }, []);

  useEffect(() => {
    if (filters.from_date && filters.to_date) {
      fetchAttendanceReport();
    }
  }, [filters]);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchSemesters = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/semesters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSemesters(response.data);
    } catch (error) {
      console.error('Error fetching semesters:', error);
    }
  };

  const fetchSections = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/sections`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSections(response.data);
    } catch (error) {
      console.error('Error fetching sections:', error);
    }
  };

  const fetchAttendanceReport = async () => {
    setLoading(true);
    try {
      // Fetch attendance records
      const params = new URLSearchParams();
      if (filters.from_date) params.append('from_date', filters.from_date);
      if (filters.to_date) params.append('to_date', filters.to_date);
      if (filters.course_id) params.append('course_id', filters.course_id);
      if (filters.section_id) params.append('section_id', filters.section_id);

      const attendanceResponse = await axios.get(`${API_URL}/api/attendance/report?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Fetch students - only filter by course, not semester/section
      // We'll filter by attendance records instead
      const studentParams = new URLSearchParams();
      if (filters.course_id) studentParams.append('course_id', filters.course_id);

      const studentsResponse = await axios.get(`${API_URL}/api/students/?${studentParams.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Process attendance data
      const attendanceRecords = attendanceResponse.data;
      const studentsData = studentsResponse.data;

      // Group attendance by student
      const attendanceByStudent = {};
      attendanceRecords.forEach(record => {
        if (!attendanceByStudent[record.student_id]) {
          attendanceByStudent[record.student_id] = {
            present: 0,
            absent: 0,
            total: 0
          };
        }
        if (record.status === 'Present') {
          attendanceByStudent[record.student_id].present++;
        } else {
          attendanceByStudent[record.student_id].absent++;
        }
        attendanceByStudent[record.student_id].total++;
      });

      // Combine student info with attendance stats
      let combinedData = studentsData.map(student => ({
        ...student,
        attendance: attendanceByStudent[student.id] || { present: 0, absent: 0, total: 0 }
      }));

      // Filter by semester and section if selected AND students have valid IDs
      // Only filter if there's at least one student with a valid (non-empty) ID
      if (filters.semester_id) {
        const hasValidSemesterIds = combinedData.some(s => s.semester_id && s.semester_id !== '');
        if (hasValidSemesterIds) {
          combinedData = combinedData.filter(student => student.semester_id === filters.semester_id);
        }
        // If no students have semester_id, show all (don't filter)
      }
      
      if (filters.section_id) {
        const hasValidSectionIds = combinedData.some(s => s.section_id && s.section_id !== '');
        if (hasValidSectionIds) {
          combinedData = combinedData.filter(student => student.section_id === filters.section_id);
        }
        // If no students have section_id, show all (don't filter)
      }

      setStudents(studentsData);
      setAttendanceData(combinedData);
    } catch (error) {
      console.error('Error fetching attendance report:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredStudents = attendanceData.filter(student =>
    student.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.admission_number.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredSemesters = semesters.filter(sem => 
    !filters.course_id || sem.course_id === filters.course_id
  );

  // Calculate overall stats
  const totalStudents = filteredStudents.length;
  const totalPresent = filteredStudents.reduce((sum, student) => sum + (student.attendance?.present || 0), 0);
  const totalAbsent = filteredStudents.reduce((sum, student) => sum + (student.attendance?.absent || 0), 0);
  const totalDays = filteredStudents.reduce((sum, student) => sum + (student.attendance?.total || 0), 0);
  
  // Calculate average attendance based on ALL students
  // Students with no attendance data are counted as 0% (encourages proper attendance marking)
  const averageAttendance = (() => {
    if (totalStudents === 0) return 0;
    
    const sumOfPercentages = filteredStudents.reduce((sum, student) => {
      // If student has attendance data, calculate their percentage
      // If no data (total = 0), count as 0% attendance
      const total = student.attendance?.total || 0;
      const present = student.attendance?.present || 0;
      const percentage = total > 0 ? (present / total) * 100 : 0;
      return sum + percentage;
    }, 0);
    
    return (sumOfPercentages / totalStudents).toFixed(1);
  })();

  const exportReport = () => {
    alert('Exporting attendance report...');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <div className="bg-blue-600 p-2 rounded-lg">
                  <Calendar className="h-7 w-7 text-white" />
                </div>
                Attendance Report
              </h1>
              <p className="text-gray-600 mt-2">View detailed attendance records from Class Attendance system</p>
            </div>
            <button
              onClick={exportReport}
              className="flex items-center gap-2 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 shadow-lg font-semibold"
            >
              <Download className="h-5 w-5" />
              Export Report
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-6 rounded-xl shadow-lg text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Total Students</p>
                <p className="text-4xl font-bold mt-2">{totalStudents}</p>
              </div>
              <div className="bg-white/20 p-3 rounded-lg">
                <Users className="h-8 w-8" />
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-green-500 to-green-600 p-6 rounded-xl shadow-lg text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium">Average Attendance</p>
                <p className="text-4xl font-bold mt-2">{averageAttendance}%</p>
              </div>
              <div className="bg-white/20 p-3 rounded-lg">
                <TrendingUp className="h-8 w-8" />
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-6 rounded-xl shadow-lg text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium">Total Present</p>
                <p className="text-4xl font-bold mt-2">{totalPresent}</p>
              </div>
              <div className="bg-white/20 p-3 rounded-lg">
                <Calendar className="h-8 w-8" />
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-red-500 to-red-600 p-6 rounded-xl shadow-lg text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm font-medium">Total Absent</p>
                <p className="text-4xl font-bold mt-2">{totalAbsent}</p>
              </div>
              <div className="bg-white/20 p-3 rounded-lg">
                <Calendar className="h-8 w-8" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-blue-100 p-2 rounded-lg">
              <Filter className="h-5 w-5 text-blue-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900">Filters</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                From Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={filters.from_date}
                onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                To Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={filters.to_date}
                onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Course</label>
              <select
                className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                value={filters.course_id}
                onChange={(e) => setFilters({ ...filters, course_id: e.target.value, semester_id: '' })}
              >
                <option value="">All Courses</option>
                {courses.map(course => (
                  <option key={course.id} value={course.id}>{course.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Semester</label>
              <select
                className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                value={filters.semester_id}
                onChange={(e) => setFilters({ ...filters, semester_id: e.target.value })}
                disabled={!filters.course_id}
              >
                <option value="">All Semesters</option>
                {filteredSemesters.map(semester => (
                  <option key={semester.id} value={semester.id}>{semester.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Section</label>
              <select
                className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                value={filters.section_id}
                onChange={(e) => setFilters({ ...filters, section_id: e.target.value })}
              >
                <option value="">All Sections</option>
                {sections.map(section => (
                  <option key={section.id} value={section.id}>{section.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search student..."
                  className="pl-10 pr-4 py-2.5 w-full border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Report Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {!filters.from_date || !filters.to_date ? (
            <div className="p-12 text-center">
              <Calendar className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-700 font-semibold text-lg">Select Date Range</p>
              <p className="text-gray-500 mt-2">Please select From Date and To Date to view attendance report</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">S.No</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Student Name</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Admission No.</th>
                      <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">Present Days</th>
                      <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">Absent Days</th>
                      <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">Total Days</th>
                      <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">Attendance %</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {loading ? (
                      <tr><td colSpan="7" className="px-6 py-12 text-center">
                        <div className="flex items-center justify-center gap-3">
                          <div className="w-6 h-6 border-3 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                          <span className="text-gray-600 font-medium">Loading attendance data...</span>
                        </div>
                      </td></tr>
                    ) : filteredStudents.length === 0 ? (
                      <tr><td colSpan="7" className="px-6 py-12 text-center">
                        <Users className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-gray-700 font-semibold text-lg">No Students Found</p>
                        <p className="text-gray-500 text-sm mt-2">
                          {filters.section_id ? (
                            <>No students are enrolled in the selected section with attendance records in this date range.</>
                          ) : (
                            <>No attendance records found for the selected date range. Try adjusting your filters.</>
                          )}
                        </p>
                      </td></tr>
                    ) : (
                      filteredStudents.map((student, index) => {
                        const attendance = student.attendance || { present: 0, absent: 0, total: 0 };
                        const percentage = attendance.total > 0 ? ((attendance.present / attendance.total) * 100).toFixed(1) : 0;
                        return (
                          <tr key={student.id} className="hover:bg-blue-50 transition-colors">
                            <td className="px-6 py-4">
                              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-semibold text-sm">
                                {index + 1}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm font-semibold text-gray-900">{student.full_name}</div>
                              <div className="text-xs text-gray-500 mt-1">{student.email}</div>
                            </td>
                            <td className="px-6 py-4">
                              <span className="inline-flex px-3 py-1 rounded-full bg-gray-100 text-sm font-medium text-gray-700">
                                {student.admission_number}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <span className="inline-flex items-center px-3 py-1 rounded-lg bg-green-100 text-green-800 text-sm font-semibold">
                                {attendance.present}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <span className="inline-flex items-center px-3 py-1 rounded-lg bg-red-100 text-red-800 text-sm font-semibold">
                                {attendance.absent}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <span className="inline-flex items-center px-3 py-1 rounded-lg bg-blue-100 text-blue-800 text-sm font-semibold">
                                {attendance.total}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <div className="flex flex-col items-center gap-1">
                                <span className={`text-lg font-bold ${
                                  percentage >= 75 ? 'text-green-600' :
                                  percentage >= 50 ? 'text-yellow-600' :
                                  'text-red-600'
                                }`}>
                                  {percentage}%
                                </span>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                  <div 
                                    className={`h-2 rounded-full ${
                                      percentage >= 75 ? 'bg-green-600' :
                                      percentage >= 50 ? 'bg-yellow-600' :
                                      'bg-red-600'
                                    }`}
                                    style={{ width: `${percentage}%` }}
                                  />
                                </div>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AttendanceReport;